import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface SegmentedOption {
  value: string
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  disabled?: boolean
}

export interface SegmentedOptionGroupProps extends Omit<BoxProps, 'onChange'> {
  options: SegmentedOption[]
  value: string
  onChange: (value: string) => void
}

const Row = styled(Box)({
  display: 'flex',
  gap: 8,
})

const Segment = styled('button', {
  shouldForwardProp: (p) => p !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  flex: 1,
  padding: '13px 15px',
  borderRadius: 12,
  boxShadow: $selected ? 'inset 0 0 0 1.5px var(--ds-ink)' : 'inset 0 0 0 1px var(--ds-border)',
  background: $selected ? 'var(--ds-surface)' : 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'box-shadow var(--ds-duration-fast) var(--ds-ease), background var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': {
    boxShadow: $selected ? 'inset 0 0 0 1.5px var(--ds-ink)' : 'inset 0 0 0 1px var(--ds-border-strong)',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  '&:focus-visible': {
    outline: '2px solid var(--ds-ink)',
    outlineOffset: 2,
  },
}))

const SegmentTitle = styled('div')({
  fontSize: 13,
  fontWeight: 650,
  display: 'flex',
  gap: 7,
  alignItems: 'center',
  color: 'var(--ds-ink)',
})

const SegmentDescription = styled('div')({
  fontSize: 11.5,
  color: 'var(--ds-ink-subtle)',
  marginTop: 3,
  lineHeight: 1.45,
})

/** Exclusive 2-3 option row — a richer radio-card group with icon + title + description per option. */
export function SegmentedOptionGroup({ options, value, onChange, ...rest }: SegmentedOptionGroupProps) {
  return (
    <Row role='radiogroup' {...rest}>
      {options.map((opt) => (
        <Segment
          key={opt.value}
          type='button'
          role='radio'
          aria-checked={opt.value === value}
          disabled={opt.disabled}
          $selected={opt.value === value}
          onClick={() => !opt.disabled && onChange(opt.value)}
        >
          <SegmentTitle>
            {opt.icon}
            {opt.title}
          </SegmentTitle>
          {opt.description && <SegmentDescription>{opt.description}</SegmentDescription>}
        </Segment>
      ))}
    </Row>
  )
}
